import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * VV cart drawer "Complete the look": loads Shopify recommendations when no products were picked,
 * and adds products to the cart with the theme's own cart events (drawer and cart count update as usual).
 */
const cache = new Map();
const norm = (el) => (el.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ');
const titleOf = (row) => {
  const t = row.querySelector('.vv-cu__title');
  return t ? norm(t) : '';
};

/* Cart drawer: one option per line ("SIZE: S"), without the theme's trailing commas */
const KEEP_OPTIONS = /^(colou?r|couleur|size|taille|pointure)/i;
function tidyVariants(root) {
  root.querySelectorAll('.cart-items__variant dd').forEach((dd) => {
    const text = dd.textContent;
    const clean = text.replace(/[,\s ]+$/, '');
    if (clean !== text) dd.textContent = clean.trim();
  });
  /* Only colour and size in the cart (hide extra options such as "Target gender") */
  root.querySelectorAll('.cart-items__variant').forEach((row) => {
    const dt = row.querySelector('dt');
    const name = dt ? dt.textContent.replace(/[:\s]+$/, '').trim() : '';
    if (name && !KEEP_OPTIONS.test(name)) {
      if (!row.hasAttribute('data-vv-hide')) row.setAttribute('data-vv-hide', '');
    }
  });
}

/* Smart picks: remember the products a shopper looks at (this browser only) */
const SEEN_KEY = 'vv_seen';
const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
function readSeen() {
  try {
    const v = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
    return Array.isArray(v) ? v.filter((x) => x && x.id && x.h) : [];
  } catch (e) {
    return [];
  }
}
function rememberProduct() {
  const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
  const m = location.pathname.match(/\/products\/([^/?#]+)/);
  if (!meta || !meta.id || !m) return;
  const list = readSeen().filter((x) => String(x.id) !== String(meta.id));
  list.unshift({ id: String(meta.id), h: decodeURIComponent(m[1]) });
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(list.slice(0, 12)));
  } catch (e) {}
}
const rowCache = new Map();
async function fetchRows(url) {
  if (rowCache.has(url)) return rowCache.get(url);
  const p = fetch(url)
    .then((r) => (r.ok ? r.text() : ''))
    .then((t) => Array.from(new DOMParser().parseFromString(t, 'text/html').querySelectorAll('[data-vv-cu-row]')))
    .catch(() => []);
  rowCache.set(url, p);
  return p;
}
const cardUrl = (h) => rootUrl() + 'products/' + encodeURIComponent(h) + '?section_id=vv-cart-card';
const recsUrl = (id, intent) =>
  rootUrl() + 'recommendations/products?product_id=' + id + '&limit=8&section_id=vv-cart-upsell-recs&intent=' + intent;

function watchDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer || drawer._vvTidy) return;
  drawer._vvTidy = true;
  /* Click on the blurred page closes the drawer */
  drawer.addEventListener('click', (e) => {
    if (e.target === drawer && typeof drawer.close === 'function') drawer.close();
  });
  tidyVariants(drawer);
  new MutationObserver(() => tidyVariants(drawer)).observe(drawer, { childList: true, subtree: true, characterData: true });
}
function boot() {
  rememberProduct();
  watchDrawer();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

async function addToCart(source, variantId, button) {
  let ok = false;
  if (!source || !source.isConnected) source = document.querySelector('#cart-drawer vv-cart-upsell') || document.getElementById('cart-drawer') || document.body;
  button.classList.add('is-loading');
  button.disabled = true;
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );
  const routes = (window.Theme && window.Theme.routes) || {};
  const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  try {
    const res = await fetch(routes.cart_add_url || rootUrl + 'cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
    });
    const data = await res.json();
    const cart = await fetch(rootUrl + 'cart.js').then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'vv-cart-upsell',
        sourceId: 'vv-cart-upsell',
        itemCount: 1,
        sections: data.sections,
        didError: Boolean(data.status),
      },
    });
    ok = !data.status;
  } catch (error) {
    deferred.reject(error);
  } finally {
    button.classList.remove('is-loading');
    button.disabled = false;
  }
  return ok;
}

/* Quick-add sheet: opens from the "+" on a card, size grid, then ADD TO CART */
const ICON_X = '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M1 1l10 10M11 1L1 11"/></svg>';
const sheet = {
  el: null,
  source: null,
  chosen: null,
  build() {
    const dialog = document.querySelector('#cart-drawer dialog');
    if (!dialog) return null;
    if (this.el && this.el.isConnected && this.el.parentElement === dialog) return this.el;
    const el = document.createElement('div');
    el.className = 'vv-sheet';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<div class="vv-sheet__veil" data-vv-sheet-close></div>' +
      '<div class="vv-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="vv-sheet-title">' +
      '<div class="vv-sheet__head"><div><p class="vv-sheet__title" id="vv-sheet-title"></p><p class="vv-sheet__sub"></p></div>' +
      '<button type="button" class="vv-sheet__close" data-vv-sheet-close aria-label="Close">' + ICON_X + '</button></div>' +
      '<div class="vv-sheet__sizes" role="group" aria-label="Size"></div>' +
      '<button type="button" class="vv-sheet__add">Select size</button></div>';
    el.addEventListener('click', (e) => {
      if (e.target.closest('[data-vv-sheet-close]')) return this.close();
      const size = e.target.closest('.vv-sheet__size');
      if (size && !size.disabled) {
        el.querySelectorAll('.vv-sheet__size').forEach((b) => b.setAttribute('aria-pressed', String(b === size)));
        this.chosen = size.dataset.id;
        const add = el.querySelector('.vv-sheet__add');
        add.textContent = 'Add to cart';
        add.classList.add('is-ready');
        return;
      }
      const add = e.target.closest('.vv-sheet__add');
      if (add && this.chosen) this.add(add);
    });
    dialog.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.el && this.el.classList.contains('is-open')) {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        }
      },
      true
    );
    dialog.addEventListener('cancel', (e) => {
      if (this.el && this.el.classList.contains('is-open')) {
        e.preventDefault();
        this.close();
      }
    });
    dialog.addEventListener('close', () => this.close());
    dialog.appendChild(el);
    this.el = el;
    return el;
  },
  open(row, source) {
    const el = this.build();
    if (!el) return;
    this.source = source;
    this.chosen = null;
    el.querySelector('.vv-sheet__title').textContent = row.dataset.title || '';
    el.querySelector('.vv-sheet__sub').textContent = row.dataset.sub || '';
    const grid = el.querySelector('.vv-sheet__sizes');
    grid.innerHTML = '';
    const sizes = row.querySelectorAll('[data-vv-cu-size]');
    sizes.forEach((s) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'vv-sheet__size';
      b.dataset.id = s.dataset.id;
      b.textContent = s.textContent.trim();
      b.disabled = s.disabled;
      b.setAttribute('aria-pressed', 'false');
      if (s.disabled) b.setAttribute('aria-label', b.textContent + ' (sold out)');
      grid.appendChild(b);
    });
    const avail = Array.from(grid.children).filter((b) => !b.disabled);
    const add = el.querySelector('.vv-sheet__add');
    add.textContent = 'Select size';
    add.classList.remove('is-ready', 'is-loading');
    if (avail.length === 1) avail[0].click();
    el.setAttribute('aria-hidden', 'false');
    this.wanted = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      if (this.wanted) el.classList.add('is-open');
    });
    setTimeout(() => {
      if (this.wanted) (avail[0] || add).focus({ preventScroll: true });
    }, 350);
  },
  close() {
    this.wanted = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (!this.el) return;
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    if (this.el.contains(document.activeElement)) document.activeElement.blur();
  },
  async add(btn) {
    btn.textContent = 'Adding…';
    const ok = await addToCart(this.source, this.chosen, btn);
    btn.textContent = ok ? 'Added' : 'Add to cart';
    if (ok) this.close();
  },
};

/* Speed: load cart picks only when the cart is opened (or about to be), not on every page view */
const CART_TRIGGERS = 'cart-icon, .header-actions__cart-icon, [href$="/cart"], [aria-controls="cart-drawer"], [data-testid="cart-icon"]';
function whenShown(target, fn) {
  let done = false;
  const go = () => {
    if (done) return;
    done = true;
    io.disconnect();
    document.removeEventListener('pointerover', warm, true);
    document.removeEventListener('touchstart', warm, true);
    document.removeEventListener('focusin', warm, true);
    fn();
  };
  const warm = (e) => {
    if (e.target && e.target.closest && e.target.closest(CART_TRIGGERS)) go();
  };
  const io = new IntersectionObserver((entries) => {
    if (entries.some((e) => e.isIntersecting)) go();
  });
  const shown = target.checkVisibility ? target.checkVisibility() : target.offsetParent !== null;
  if (shown) return go();
  io.observe(target);
  document.addEventListener('pointerover', warm, true);
  document.addEventListener('touchstart', warm, { capture: true, passive: true });
  document.addEventListener('focusin', warm, true);
}

function flash(btn) {
  btn.classList.add('is-done');
  setTimeout(() => btn.classList.remove('is-done'), 1200);
}

class VVCartUpsell extends HTMLElement {
  static get observedAttributes() {
    return ['data-key'];
  }

  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this.addEventListener('click', async (e) => {
      const size = e.target.closest('[data-vv-cu-size]');
      if (size) {
        e.preventDefault();
        if (!size.disabled && size.dataset.id) addToCart(this, size.dataset.id, size);
        return;
      }
      const open = e.target.closest('[data-vv-cu-open]');
      if (open) {
        e.preventDefault();
        const row = open.closest('[data-vv-cu-row]');
        if (!row) return;
        if (row.hasAttribute('data-single') || !row.querySelector('[data-vv-cu-size]')) {
          const input = row.querySelector('[data-vv-cu-variant]');
          if (input && input.value && (await addToCart(this, input.value, open))) flash(open);
        } else {
          sheet.open(row, this);
        }
        return;
      }
      const btn = e.target.closest('[data-vv-cu-add]');
      if (!btn) return;
      e.preventDefault();
      const row = btn.closest('[data-vv-cu-row]');
      const input = row && row.querySelector('[data-vv-cu-variant]');
      if (input && input.value) addToCart(this, input.value, btn);
    });
    this._observer = new MutationObserver(() => this.ensure());
    this._observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
    whenShown(this.closest('dialog') || this.parentElement || this, () => {
      this._ready = true;
      this.ensure();
      if (this.hasAttribute('data-smart')) this.smartEmpty();
    });
  }

  /* Empty cart: products the shopper viewed + similar ones first, then the default SALE / PRE FALL picks */
  async smartEmpty() {
    const seen = readSeen();
    if (!seen.length || this._smart) return;
    this._smart = true;
    try {
      const lists = await Promise.all([
        ...seen.slice(0, 3).map((x) => fetchRows(cardUrl(x.h))),
        fetchRows(recsUrl(seen[0].id, 'related')),
      ]);
      const list = this.list;
      if (!list) return;
      const picked = [];
      const ids = new Set();
      const add = (row) => {
        const id = row.dataset.pid;
        if (!id || ids.has(id) || picked.length >= 10) return;
        ids.add(id);
        picked.push(document.importNode(row, true));
      };
      lists.flat().forEach(add);
      if (!picked.length) return;
      Array.from(list.children).forEach(add);
      this._observer.disconnect();
      list.replaceChildren(...picked);
      list.scrollLeft = 0;
      const h = this.querySelector('[data-vv-cu-heading]');
      if (h) h.textContent = 'Picked for you';
      this._observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
    } catch (e) {
      // keep the default picks
    }
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._bound = false;
  }

  attributeChangedCallback() {
    if (this._bound) this.ensure();
  }

  get list() {
    return this.querySelector('[data-vv-cu-list]');
  }

  ensure() {
    if (!this._ready) return;
    const url = this.dataset.recsUrl;
    const list = this.list;
    if (!list) return;
    if (!url) {
      if (this.hidden && list.children.length) this.hidden = false;
      return;
    }
    if (list.children.length) {
      if (this.hidden) this.hidden = false;
      return;
    }
    this.load(url);
  }

  async load(url) {
    const key = url + '|' + this.dataset.exclude;
    if (this._loading === key) return;
    this._loading = key;
    try {
      let rows = cache.get(key);
      if (!rows) {
        rows = [];
        const exclude = this.dataset.exclude || '';
        const max = Number(this.dataset.max || 6);
        const taken = new Set(
          Array.from(document.querySelectorAll('#cart-drawer .cart-items__title, #cart-drawer .vv-cu__title')).map(norm)
        );
        for (const intent of ['complementary', 'related']) {
          const res = await fetch(url + '&intent=' + intent);
          if (!res.ok) continue;
          const html = new DOMParser().parseFromString(await res.text(), 'text/html');
          html.querySelectorAll('[data-vv-cu-row]').forEach((row) => {
            const id = row.dataset.pid;
            const name = titleOf(row);
            if (rows.length >= max || exclude.includes('|' + id + '|') || rows.some((r) => r.dataset.pid === id)) return;
            if (name && (taken.has(name) || rows.some((r) => titleOf(r) === name))) return;
            rows.push(row);
          });
          if (rows.length >= max) break;
        }
        /* Not enough matches: add products the shopper looked at (not already in the cart) */
        for (const x of readSeen().slice(0, 6)) {
          if (rows.length >= max) break;
          if (exclude.includes('|' + x.id + '|') || rows.some((r) => r.dataset.pid === x.id)) continue;
          const found = await fetchRows(cardUrl(x.h));
          const row = found[0];
          if (row && !taken.has(titleOf(row))) rows.push(row);
        }
        cache.set(key, rows);
      }
      const list = this.list;
      if (list && !list.children.length && rows.length) {
        this._observer.disconnect();
        rows.forEach((row) => list.appendChild(document.importNode(row, true)));
        this.hidden = false;
        this._observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
      }
    } catch (e) {
      // keep the block hidden if recommendations fail
    } finally {
      this._loading = null;
    }
  }
}

if (!customElements.get('vv-cart-upsell')) customElements.define('vv-cart-upsell', VVCartUpsell);